import {
  UUITextAreaVariant,
  UUITextAreaSize,
  UUITextAreaStyle,
  UUITextAreaSkin,
  UUITextAreaTheme
} from '../../../types/UUITextArea'

const baseClassName = (size: UUITextAreaSize = 'normal'): string => {
  const base = [
    // common styles
    'tw:box-border tw:rounded-xl tw:py-1.5 tw:h-full tw:pr-3 tw:pl-4 tw:border tw:w-full tw:font-ubuntu tw:outline-2 tw:outline-transparent tw:text-grey-50',
    //aria-disabled
    'tw:aria-disabled:shadow-none tw:aria-disabled:bg-grey-900 tw:aria-disabled:border-grey-900 tw:aria-disabled:cursor-default',
    // disabled
    'tw:has-[textarea:disabled]:shadow-none tw:has-[textarea:disabled]:bg-grey-900 tw:has-[textarea:disabled]:border-grey-900 tw:has-[textarea:disabled]:cursor-default',
    // read-only
    'tw:has-[textarea:read-only]:shadow-none tw:has-[textarea:read-only]:bg-grey-800 tw:has-[textarea:read-only]:border-grey-800',
    // transitions
    'tw:transition-all tw:duration-300 tw:ease-in-out'
  ].join(' ')
  let sizeClassName = ''
  switch (size) {
    case 'small': {
      sizeClassName = 'tw:text-xs tw:font-normal tw:leading-4 tw:py-1 tw:pr-3 tw:pl-4'
      break
    }
    case 'large': {
      sizeClassName = 'tw:text-base tw:font-normal tw:leading-6 tw:py-2 tw:pr-3 tw:pl-4'
      break
    }
    default:
    case 'normal': {
      sizeClassName = 'tw:text-sm tw:font-normal tw:leading-5 tw:py-1.5 tw:pr-3 tw:pl-4'
      break
    }
  }
  return `${base} ${sizeClassName}`
}

const styleClassName = (
  style: UUITextAreaStyle = 'filled',
  variant: UUITextAreaVariant = 'default',
  skin: UUITextAreaSkin = 'default'
): string => {
  let styleClassName = ''
  let variantClassName = ''
  if ('filled' === style) {
    styleClassName = [
      'tw:text-grey-50 tw:border-grey-1000 tw:shadow-[0px_1px_8px_0px_#737D8C26]',
      // hover
      'tw:hover:border-aquamarine-400 tw:hover:shadow-[0px_1px_5px_0px_#5BBFC059]',
      // focus-within
      'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_2px_#29ABE259]',
      // hover + focus-within (override hover)
      'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#29ABE259]'
    ].join(' ')
    switch (variant) {
      case 'default': {
        variantClassName = ['tw:bg-grey-1000'].join(' ')
        break
      }
      case 'invalid': {
        variantClassName = [
          'tw:shadow-[0px_1px_8px_0px_#E2294E26] tw:text-red-50',
          // hover
          'tw:hover:border-red-100 tw:hover:shadow-[0px_1px_8px_0px_#E2294E26]',
          // focus-within
          'tw:focus-within:border-red-50 tw:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:focus-within:text-grey-50',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-red-50 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:hover:focus-within:text-grey-50'
        ].join(' ')
        break
      }
      case 'warning': {
        variantClassName = [
          ' tw:border-yellow-10',
          // hover
          'tw:hover:border-yellow-25 tw:hover:shadow-none',
          // focus-within
          'tw:focus-within:border-yellow-10 tw:focus-within:shadow-[0px_0px_0px_3px_#FFAF34]',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-yellow-10 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#FFAF34]'
        ].join(' ')
        break
      }
    }
  } else if ('outline' === style) {
    const baseOutlineStyles = [
      'tw:text-grey-50 tw:border-grey-700',
      // aria-disabled
      'tw:aria-disabled:bg-grey-800 tw:aria-disabled:border-grey-800 tw:aria-disabled:cursor-not-allowed',
      // disabled
      'tw:disabled:bg-grey-800 tw:disabled:border-grey-800',
      // read-only
      'tw:has-[textarea:read-only]:bg-grey-800 tw:has-[textarea:read-only]:border-grey-700'
    ].join(' ')

    switch (variant) {
      case 'default': {
        variantClassName =
          skin === 'account'
            ? [
                'tw:bg-grey-1000 tw:!border-aquamarine-100 tw:text-primary-text',
                // hover
                'tw:hover:!border-aquamarine-400',
                // focus-within
                'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]',
                // hover + focus-within (override hover)
                'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]'
              ].join(' ')
            : [
                'tw:bg-grey-1000',
                // hover
                'tw:hover:border-aquamarine-400',
                // focus-within
                'tw:focus-within:border-blue-100 tw:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]',
                // hover + focus-within (override hover)
                'tw:hover:focus-within:border-blue-100 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#29ABE259]'
              ].join(' ')
        break
      }
      case 'invalid': {
        variantClassName = [
          'tw:border-red-50 tw:text-red-50',
          // hover
          'tw:hover:border-red-100 tw:hover:shadow-[0px_1px_8px_0px_#E2294E26]',
          // focus-within
          'tw:focus-within:border-red-50 tw:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:focus-within:text-grey-50',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-red-50 tw:hover:focus-within:shadow-[0px_0px_0px_2px_#E2294E59] tw:hover:focus-within:text-grey-50'
        ].join(' ')
        break
      }
      case 'warning': {
        variantClassName = [
          'tw:border-yellow-10',
          // hover
          'tw:hover:border-yellow-10 tw:hover:shadow-none',
          // focus-within
          'tw:focus-within:border-yellow-10 tw:focus-within:shadow-[0px_0px_0px_3px_#FFAF3459]',
          // hover + focus-within (override hover)
          'tw:hover:focus-within:border-yellow-10 tw:hover:focus-within:shadow-[0px_0px_0px_3px_#FFAF3459]'
        ].join(' ')
        break
      }
    }

    styleClassName = baseOutlineStyles
  }
  return `${styleClassName} ${variantClassName}`
}

const styleTextAreaClassName = (variant: UUITextAreaVariant = 'default'): string => {
  const base = [
    'tw:group-aria-disabled:pointer-events-none tw:flex-1 tw:min-w-0 tw:bg-transparent tw:border-none tw:outline-none tw:text-inherit tw:disabled:placeholder:text-grey-400  ',

    'tw:resize-none tw:overflow-hidden  tw:disabled:cursor-default tw:read-only:cursor-text'
  ].join(' ')
  let variantClassName = ''
  switch (variant) {
    case 'default': {
      variantClassName = ['tw:placeholder:text-grey-500'].join(' ')
      break
    }
    case 'invalid': {
      variantClassName = ['tw:placeholder:text-red-25'].join(' ')
      break
    }
    case 'warning': {
      variantClassName = ['tw:placeholder:text-grey-500'].join(' ')
      break
    }
  }
  return `${base} ${variantClassName}`
}

const groupClassName = (): string =>
  'tw:disabled:cursor-not-allowed tw:group-aria-disabled:pointer-events-none tw:group-aria-disabled:cursor-not-allowed tw:text-grey-600 tw:hover:text-grey-300 tw:transition-all tw:duration-300 tw:ease-in-out'

const textAreaTheme: UUITextAreaTheme = {
  element: ({ size, style, variant, skin }) =>
    `tw:flex tw:group ${baseClassName(size)} ${styleClassName(style, variant, skin)}`,
  textArea: ({ variant }) => `${styleTextAreaClassName(variant)}`,
  leftIcon: () => `${groupClassName()} tw:mr-2 `,
  rightIcon: () => `${groupClassName()} tw:ml-2 tw:cursor-pointer `,
  counter: () => `${groupClassName()} tw:text-sm tw:text-grey-500 tw:ml-2 tw:hover:text-grey-500 tw:!text-[10px]`,
  leftAddons: () => `${groupClassName()} tw:mr-2 tw:flex tw:cursor-pointer `,
  rightAddons: () => `${groupClassName()} tw:ml-2 tw:flex tw:cursor-pointer`
}

export default textAreaTheme
